import { formatAgentName } from "../upcomingListings/listingDisplay";

/**
 * Formatters for the open-house surfaces — the dashboard button, the Open
 * Houses tab, and whatever mobile grows in phase 2.
 *
 * The host status is derived in exactly one place. The three states are not
 * interchangeable and the difference is the whole point of the feature:
 * "nobody is booked and the agent asked for cover" is a gap a brokerage has to
 * fill this week, while "the agent never asked" is not. Collapsing them would
 * either invent work or hide it.
 */

export const HOST_STATUS = {
  ASSIGNED: "assigned",
  NEEDED: "needed",
  NOT_NEEDED: "not-needed",
};

export const hostStatus = (occurrence) => {
  const host = occurrence?.host;

  if (host) {
    const name = formatAgentName(host);
    return {
      kind: HOST_STATUS.ASSIGNED,
      // Never "Host: " with nothing after it — a user row can arrive with no
      // name at all.
      label: name ? `Host: ${name}` : "Host assigned",
      hostName: name,
      isGap: false,
    };
  }

  if (occurrence?.needHost) {
    return {
      kind: HOST_STATUS.NEEDED,
      label: "Host Needed · Still Available",
      hostName: null,
      isGap: true,
    };
  }

  return {
    kind: HOST_STATUS.NOT_NEEDED,
    label: "No host needed",
    hostName: null,
    isGap: false,
  };
};

const DATE_OPTS = { weekday: "short", month: "short", day: "numeric" };
const TIME_OPTS = { hour: "numeric", minute: "2-digit", hour12: true };

const parse = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "Sat, Sep 12 · 1:00 – 4:00 PM", or null when the start will not parse. */
export const formatSlotTime = (occurrence) => {
  const start = parse(occurrence?.startTime);
  if (!start) return null;

  const end = parse(occurrence?.endTime);

  return [
    start.toLocaleDateString("en-US", DATE_OPTS),
    end
      ? `${start.toLocaleTimeString("en-US", TIME_OPTS)} – ${end.toLocaleTimeString("en-US", TIME_OPTS)}`
      : start.toLocaleTimeString("en-US", TIME_OPTS),
  ].join(" · ");
};

/** "Sat, Sep 12" — the compact form the dashboard and narrow rows use. */
export const formatSlotDay = (occurrence) => {
  const start = parse(occurrence?.startTime);
  return start ? start.toLocaleDateString("en-US", DATE_OPTS) : null;
};

/** "1:00 – 4:00 PM" split out so a row can stack the day above the range. */
export const formatSlotRange = (occurrence) => {
  const start = parse(occurrence?.startTime);
  if (!start) return null;
  const end = parse(occurrence?.endTime);
  return end
    ? `${start.toLocaleTimeString("en-US", TIME_OPTS)} – ${end.toLocaleTimeString("en-US", TIME_OPTS)}`
    : start.toLocaleTimeString("en-US", TIME_OPTS);
};
